"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

const ACTIONS = [
  {
    label: "The Blacklist",
    icon: "ShieldExclamationIcon",
    anchor: "blacklist",
    accent: "#111111",
    description: "See guilty brands",
    bgFrom: "#111111",
    bgTo: "#333333",
    textColor: "text-white",
  },
  {
    label: "Join the Movement",
    icon: "UserGroupIcon",
    anchor: "community",
    accent: "#FF2222",
    description: "Add your name",
    bgFrom: "#FF2222",
    bgTo: "#FF6B6B",
    textColor: "text-white",
  },
  {
    label: "Switch Brands",
    icon: "ArrowsRightLeftIcon",
    anchor: "toolkit",
    accent: "#CC0000",
    description: "Protest toolkit",
    bgFrom: "#CC0000",
    bgTo: "#FF2222",
    textColor: "text-white",
  },
  {
    label: "Tweet It",
    icon: "MegaphoneIcon",
    anchor: "toolkit",
    accent: "#374151",
    description: "Copy the script",
    bgFrom: "#374151",
    bgTo: "#6B7280",
    textColor: "text-white",
  },
];

interface ActionItem {
  label: string;
  icon: string;
  anchor: string;
  accent: string;
  description: string;
  bgFrom: string;
  bgTo: string;
  textColor: string;
}

interface ActionSliderProps {
  items: ActionItem[];
  scrollTo: (id: string) => void;
}

function ActionSlider({ items, scrollTo }: ActionSliderProps) {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [visible, setVisible] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const autoAdvanceRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback(
    (index: number, dir: "left" | "right" = "right") => {
      if (isAnimating) return;
      setIsAnimating(true);
      setDirection(dir);
      setVisible(false);
      setTimeout(() => {
        setCurrent(index);
        setVisible(true);
        setIsAnimating(false);
      }, 220);
    },
    [isAnimating]
  );

  const prev = useCallback(() => {
    goTo((current - 1 + items.length) % items.length, "left");
  }, [current, items.length, goTo]);

  const next = useCallback(() => {
    goTo((current + 1) % items.length, "right");
  }, [current, items.length, goTo]);

  // Auto-advance timer
  useEffect(() => {
    autoAdvanceRef.current = setInterval(() => {
      next();
    }, 3500);
    return () => {
      if (autoAdvanceRef.current) clearInterval(autoAdvanceRef.current);
    };
  }, [next]);

  const resetTimer = () => {
    if (autoAdvanceRef.current) clearInterval(autoAdvanceRef.current);
    autoAdvanceRef.current = setInterval(() => {
      next();
    }, 3500);
  };

  const handlePrev = () => { resetTimer(); prev(); };
  const handleNext = () => { resetTimer(); next(); };
  const handleDot = (i: number) => {
    resetTimer();
    goTo(i, i > current ? "right" : "left");
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      resetTimer();
      diff > 0 ? next() : prev();
    }
    touchStartX.current = null;
  };

  const action = items[current];
  const slideClass = visible
    ? "opacity-100 translate-x-0"
    : direction === "right" ?"opacity-0 -translate-x-6" :"opacity-0 translate-x-6";

  return (
    <div className="w-full">
      {/* Mobile slider */}
      <div className="md:hidden">
        <div
          className="overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={`transition-all duration-200 ease-out ${slideClass}`}
          >
            <button
              onClick={() => { scrollTo(action.anchor); }}
              style={{ background: `linear-gradient(135deg, ${action.bgFrom}, ${action.bgTo})` }}
              className="group w-full flex items-center gap-3 rounded-2xl px-4 py-4 text-left shadow-lg active:scale-95 transition-transform duration-150"
              aria-label={`Go to ${action.label}`}
            >
              <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-colors duration-200">
                <Icon name={action.icon} size={22} variant="outline" className="text-white" />
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-base font-extrabold text-white leading-tight">{action.label}</span>
                <span className="text-xs font-semibold text-white/80 mt-0.5">{action.description}</span>
              </span>
              <span className="ml-auto flex-shrink-0 w-8 h-8 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-all duration-200 group-hover:translate-x-0.5">
                <Icon name="ChevronRightIcon" size={14} variant="outline" className="text-white" />
              </span>
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-3 px-1">
          <button
            onClick={handlePrev}
            className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:border-[#FF2222]/50 hover:bg-[#FF2222]/5 active:scale-90 transition-all duration-150"
            aria-label="Previous"
          >
            <Icon name="ChevronLeftIcon" size={14} variant="outline" className="text-muted" />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {items.map((item, i) => (
              <button
                key={i}
                onClick={() => handleDot(i)}
                style={i === current ? { background: item.bgFrom } : {}}
                className={`rounded-full transition-all duration-300 ${
                  i === current
                    ? "w-6 h-2.5 scale-110" :"w-2 h-2 bg-ink/20 hover:bg-ink/40"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:border-[#FF2222]/50 hover:bg-[#FF2222]/5 active:scale-90 transition-all duration-150"
            aria-label="Next"
          >
            <Icon name="ChevronRightIcon" size={14} variant="outline" className="text-muted" />
          </button>
        </div>
      </div>

      {/* Desktop: all 4 cards in a row */}
      <div className="hidden md:grid md:grid-cols-4 gap-3">
        {items.map((action) => (
          <button
            key={action.label}
            onClick={() => scrollTo(action.anchor)}
            style={{ background: `linear-gradient(135deg, ${action.bgFrom}, ${action.bgTo})` }}
            className="group flex items-center gap-3 rounded-2xl px-4 py-3.5 text-left shadow-md hover:shadow-xl hover:scale-[1.04] active:scale-[0.97] transition-all duration-200"
            aria-label={`Go to ${action.label}`}
          >
            <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-colors duration-200">
              <Icon name={action.icon} size={18} variant="outline" className="text-white" />
            </span>
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-extrabold text-white leading-tight truncate">{action.label}</span>
              <span className="text-xs font-bold text-white/80 mt-0.5">{action.description}</span>
            </span>
            <span className="ml-auto flex-shrink-0 w-7 h-7 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-all duration-200 group-hover:translate-x-0.5">
              <Icon name="ChevronRightIcon" size={13} variant="outline" className="text-white" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const items = el.querySelectorAll(".hero-animate");
    items.forEach((item, i) => {
      (item as HTMLElement).style.animationDelay = `${i * 0.12}s`;
      item.classList.add("animate-fade-up");
    });
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToToolkit = () => scrollTo("toolkit");
  const scrollToBoycott = () => scrollTo("target-banner");

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen bg-surface overflow-hidden flex flex-col"
      aria-label="Hero section"
    >
      {/* Noise overlay */}
      <div className="noise-overlay absolute inset-0 z-0 pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 py-6">
        <AppLogo
          text="PaidMeansPaid"
          iconName="ShieldCheckIcon"
          size={36}
          className="cursor-pointer"
        />
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-mid">
          <a href="#how-it-works" className="hover:text-ink transition-colors">The Logic</a>
          <a href="#toolkit" className="hover:text-ink transition-colors">Toolkit</a>
          <a href="#community" className="hover:text-ink transition-colors">Community</a>
        </div>
        <button
          onClick={scrollToBoycott}
          className="flex items-center gap-2 bg-[#FF2222] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#CC0000] hover:scale-105 transition-all duration-300 btn-red-pulse"
          aria-label="Join the boycott"
        >
          <span className="blink-alert inline-block w-2 h-2 rounded-full bg-white" />
          Join the Boycott
        </button>
      </nav>

      {/* Hero body */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-12 pt-8 pb-24 max-w-8xl mx-auto w-full">

        {/* Eyebrow */}
        <div className="hero-animate opacity-0-init mb-6 flex items-center gap-3">
          <span className="bg-[#FF2222] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            Consumer Uprising
          </span>
          <span className="text-muted text-sm font-medium">#PaidMeansPaid</span>
        </div>

        {/* Headline */}
        <h1 className="hero-animate opacity-0-init font-display text-[clamp(3rem,10vw,8.5rem)] leading-[0.92] tracking-tightest text-ink mb-6 max-w-5xl">
          If We Pay,<br />
          <span className="italic font-normal text-[#FF2222]">They Go Away.</span>
        </h1>

        {/* Sub-headline */}
        <p className="hero-animate opacity-0-init text-lg md:text-xl text-mid leading-relaxed max-w-2xl mb-10 font-light">
          Streaming services charge you a monthly fee{" "}
          <strong className="font-semibold text-ink">AND</strong> sell your attention to advertisers.
          We don&apos;t boycott the streamers — we boycott the advertisers who enable it.
          Stop the double-dip.
        </p>

        {/* CTAs */}
        <div className="hero-animate opacity-0-init flex flex-wrap gap-4 items-center">
          <button
            onClick={scrollToToolkit}
            className="group flex items-center gap-3 bg-[#FF2222] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#CC0000] hover:scale-105 transition-all duration-300 btn-red-pulse"
            aria-label="Download the protest toolkit"
          >
            <Icon name="ArrowDownTrayIcon" size={18} variant="outline" />
            Download the Protest Toolkit
            <Icon name="ArrowRightIcon" size={16} variant="outline" className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={scrollToBoycott}
            className="flex items-center gap-2 border-2 border-ink text-ink px-8 py-4 rounded-full text-base font-semibold hover:bg-ink hover:text-surface transition-all duration-300"
            aria-label="See current target"
          >
            <Icon name="ExclamationTriangleIcon" size={18} variant="outline" />
            See Current Target
          </button>
        </div>

        {/* Quick-action anchor slider */}
        <div className="hero-animate opacity-0-init mt-10">
          <ActionSlider items={ACTIONS} scrollTo={scrollTo} />
        </div>

        {/* Stats strip */}
        <div className="hero-animate opacity-0-init mt-12 flex flex-wrap gap-10 items-center border-t border-[rgba(13,13,13,0.1)] pt-10">
          {[
            { value: "214K", label: "Advocates Active" },
            { value: "38", label: "Brands Targeted" },
            { value: "$4.2M", label: "Est. Revenue Pressured" },
            { value: "12", label: "Brands Backed Down" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="stat-number text-4xl md:text-5xl">{stat.value}</span>
              <span className="text-xs font-medium uppercase tracking-widest text-muted mt-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative large text */}
      <div
        className="absolute bottom-0 right-0 font-display font-black text-[clamp(6rem,20vw,18rem)] leading-none text-ink opacity-[0.03] select-none pointer-events-none"
        aria-hidden="true"
      >
        PAID
      </div>
    </section>
  );
}